import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { initializeApp, deleteApp } from "firebase/app";
import { auth, db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

export default function AddUser() {
  const navigate    = useNavigate();
  const currUser    = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId   = currUser?.company_id   || "";
  const companyName = currUser?.company_name || "";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", role: "technician", active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const isTechnician = form.role === "technician";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) { setError("Company ID missing. Please re-login."); return; }

    const emailToUse = form.email.trim().toLowerCase();

    if (isTechnician && !form.phone) { setError("Phone number is required."); return; }
    if (!isTechnician && !form.email) { setError("Email is required."); return; }
    if (!isTechnician && !form.password) { setError("Password is required."); return; }

    try {
      setLoading(true);
      setError("");

      // Phone normalize — always produce E.164 (+91XXXXXXXXXX) or reject.
      // Never silently store an unnormalized/raw value.
      const rawPhone = form.phone.replace(/\s+/g, "").trim();
      let digits = rawPhone.replace(/\D/g, "");
      // Strip leading "91" country code, if present (e.g. "919876543210")
      if (digits.startsWith("91") && digits.length === 12) {
        digits = digits.slice(2);
      }
      // Strip a single leading "0" local-dialing habit (e.g. "09876543210")
      if (digits.startsWith("0") && digits.length === 11) {
        digits = digits.slice(1);
      }
      if (digits.length !== 10) {
        setError("Please enter a valid 10-digit mobile number.");
        setLoading(false);
        return;
      }
      const cleanPhone = "+91" + digits;

      if (isTechnician) {
        // ── TECHNICIAN — sirf Firestore doc banao ──
        // No Firebase Auth account — OTP se first login pe auto-create hoga
        const { addDoc, collection: col } = await import("firebase/firestore");

        // Duplicate phone check
        const { getDocs: gd, query: q, where: w } = await import("firebase/firestore");
        const existing = await gd(q(
          col(db, "users"),
          w("phone",      "==", cleanPhone),
          w("company_id", "==", companyId)
        ));
        if (!existing.empty) {
          setError("Yeh phone number already registered hai is company mein.");
          return;
        }

        await addDoc(col(db, "users"), {
          uid:          "",  // OTP first login pe set hoga
          name:         form.name.trim(),
          email:        "",
          phone:        cleanPhone,
          role:         "technician",
          active:       form.active,
          status:       "active",
          company_id:   companyId,
          company_name: companyName,
          created_at:   serverTimestamp(),
        });

      } else {
        // ── NON-TECHNICIAN — Email + Password ──
        const secondaryApp  = initializeApp(auth.app.options, "secondary-" + Date.now());
        const secondaryAuth = getAuth(secondaryApp);
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth, emailToUse, form.password
        );
        const uid = userCredential.user.uid;
        await secondaryAuth.signOut();
        await deleteApp(secondaryApp);

        await setDoc(doc(db, "users", uid), {
          uid,
          name:         form.name.trim(),
          email:        emailToUse,
          phone:        cleanPhone || "",
          role:         form.role,
          active:       form.active,
          status:       "active",
          company_id:   companyId,
          company_name: companyName,
          created_at:   serverTimestamp(),
        });
      }

      navigate("/users");
    } catch (err) {
      console.error("Add User Error:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("Yeh email already registered hai.");
      } else {
        setError(err.message || "Failed to create user.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Add User</h1>

      {error && <div className="bg-red-100 text-red-600 p-3 rounded mb-4">{error}</div>}

      <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-sm text-slate-600">
        🏢 Company: <strong>{companyName || companyId}</strong>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Full Name *</label>
          <input
            type="text"
            name="name"
            autoComplete="off"
            placeholder="e.g. Rahul Sharma"
            className="border p-2 w-full rounded"
            value={form.name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Role *</label>
          <select
            name="role"
            className="border p-2 w-full rounded"
            value={form.role}
            onChange={handleChange}
          >
            <option value="owner">Owner</option>
            <option value="sm">Service Manager</option>
            <option value="manager">Manager</option>
            <option value="site_engineer">Site Engineer</option>
            <option value="technician">Technician</option>
            <option value="store">Store</option>
            <option value="accounts">Accountant</option>
          </select>
        </div>

        {/* Phone (Technician) or Email (others) */}
        {isTechnician ? (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Mobile Number *</label>
            <input
              type="tel"
              name="phone"
              autoComplete="off"
              placeholder="9876543210"
              className="border p-2 w-full rounded"
              value={form.phone}
              onChange={handleChange}
              required
            />
            {/* Info box — no password needed */}
            <div className="mt-2 bg-blue-50 border border-blue-100 rounded p-2 text-xs text-blue-600">
              ℹ️ Default password will be set to the mobile number. Technician can change it after first login.
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email Address *</label>
            <input
              type="email"
              name="email"
              autoComplete="off"
              placeholder="user@company.com"
              className="border p-2 w-full rounded"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {/* Password — only for non-technicians */}
        {!isTechnician && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Password *</label>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              placeholder="Min 6 characters"
              className="border p-2 w-full rounded"
              value={form.password}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {/* Active */}
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
          Active User
        </label>

        <button
          type="submit"
          disabled={loading}
          className={`w-full px-4 py-2 rounded text-white font-semibold ${
            loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {loading ? "Creating User..." : "Save User"}
        </button>
      </form>
    </div>
  );
}
